import { Ruler, Armchair, Waves, CircleDollarSign, BookOpen, Sparkles, Sun, UtensilsCrossed, Luggage, Baby, Handshake } from "lucide-react";
import { useLang } from "@/App";
import { all, pick } from "@/data/images";
import Hero from "@/components/Hero";
import Foto from "@/components/Foto";
import { Reveal, Pretitulo, TituloSeccion } from "@/components/Reveal";
import { useLightbox } from "@/hooks/useLightbox";

export default function Servicios() {
  const { t, L } = useLang();
  const p = t.servicios;
  const { open, portal } = useLightbox();
  const espaciosFotos = p.espacios.cards.map((c) => pick(c.foto[0], 0) || all(c.foto[0])[0]);
  const produccionFotos = p.produccion.cards.map((c) => pick(c.foto[0], 0) || all(c.foto[0])[0]);

  const FILAS_ICONOS = [Sparkles, UtensilsCrossed, Luggage, Baby, Handshake];

  return (
    <div data-testid="pagina-servicios">
      <Hero pre={p.hero.pre} titulo={p.hero.titulo} fotos={[pick(p.hero.foto[0], 0) || all(p.hero.foto[0])[0]]} testid="hero-servicios" foco="center 30%" />

      {/* 2. Presentación */}
      <section className="py-20 md:py-28" data-testid="s-presentacion">
        <div className="mx-auto max-w-3xl px-6 text-center md:px-10">
          <Reveal>
            <Pretitulo>{p.presentacion.pre}</Pretitulo>
            <h2 className="mt-4 font-serif text-3xl font-light tracking-tight text-ink md:text-5xl">{p.presentacion.titulo}</h2>
            <p className="mt-7 text-[15.5px] leading-relaxed text-ink-soft">{p.presentacion.cuerpo}</p>
          </Reveal>
        </div>
      </section>

      {/* 3. Pileta */}
      <section className="bg-sand-deep py-20 md:py-28" data-testid="s-pileta">
        <div className="mx-auto grid max-w-[1300px] items-center gap-12 px-6 md:px-10 lg:grid-cols-[7fr_5fr] lg:gap-16">
          <Reveal className="img-frame aspect-[4/3] w-full">
            <Foto src={pick(p.pileta.foto[0], 0) || all(p.pileta.foto[0])[0]} alt="" className="h-full w-full" testid="s-pileta-foto" />
          </Reveal>
          <Reveal delay={0.12}>
            <Pretitulo center={false}>{p.pileta.pre}</Pretitulo>
            <h2 className="mt-4 font-serif text-3xl font-light tracking-tight text-ink md:text-4xl">{p.pileta.titulo}</h2>
            <p className="mt-6 text-[15px] leading-relaxed text-ink-soft">{p.pileta.texto}</p>
            <div className="mt-8 grid grid-cols-2 gap-4">
              {p.pileta.datos.map(([k, v], i) => (
                <div key={k} className="border border-lineas bg-sand p-5" data-testid={`s-pileta-dato-${i}`}>
                  <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-terra">{k}</p>
                  <p className="mt-2 text-sm leading-relaxed text-ink-soft">{v}</p>
                </div>
              ))}
            </div>
          </Reveal>
        </div>
      </section>

      {/* 4. Espacios comunes */}
      <section className="py-20 md:py-28" data-testid="s-espacios">
        <div className="mx-auto max-w-[1300px] px-6 md:px-10">
          <TituloSeccion pre={p.espacios.pre} titulo={p.espacios.titulo} />
          <Reveal className="mx-auto max-w-2xl text-center">
            <p className="text-[15px] leading-relaxed text-ink-soft">{p.espacios.apoyo}</p>
          </Reveal>
          <div className="mt-14 grid gap-8 md:grid-cols-3 md:gap-6">
            {p.espacios.cards.map((c, i) => (
              <Reveal key={c.t} delay={i * 0.08} className="flex flex-col" testid={`s-espacio-card-${i}`}>
                <button type="button" onClick={() => open(espaciosFotos, i)} className="img-frame block aspect-[4/3] w-full cursor-pointer" data-testid={`s-espacio-card-${i}-foto-btn`}>
                  <Foto src={espaciosFotos[i]} alt="" className="h-full w-full" testid={`s-espacio-card-${i}-foto`} />
                </button>
                <h3 className="mt-6 font-serif text-2xl font-light text-ink">{c.t}</h3>
                <p className="mt-3 text-sm leading-relaxed text-ink-soft">{c.d}</p>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* 5. Sabores y creaciones */}
      <section className="bg-sand-deep py-20 md:py-28" data-testid="s-produccion">
        <div className="mx-auto max-w-[1400px] px-6 md:px-10">
          <TituloSeccion pre={p.produccion.pre} titulo={p.produccion.titulo} />
          <Reveal className="mx-auto mb-14 max-w-2xl text-center">
            <p className="text-[15px] leading-relaxed text-ink-soft">{p.produccion.apoyo}</p>
          </Reveal>
          <div className="grid gap-8 sm:grid-cols-2 lg:grid-cols-4 md:gap-5">
            {p.produccion.cards.map((c, i) => (
              <Reveal key={c.t} delay={i * 0.08} className="flex flex-col" testid={`s-produccion-card-${i}`}>
                <button type="button" onClick={() => open(produccionFotos, i)} className="img-frame block aspect-square w-full cursor-pointer" data-testid={`s-produccion-card-${i}-foto-btn`}>
                  <Foto src={produccionFotos[i]} alt="" className="h-full w-full" testid={`s-produccion-card-${i}-foto`} />
                </button>
                <p className="mt-5 text-[10px] font-bold uppercase tracking-[0.22em] text-terra">{c.pre}</p>
                <h3 className="mt-2 font-serif text-xl text-ink">{c.t}</h3>
                <p className="mt-2 text-[13px] leading-relaxed text-ink-soft">{c.d}</p>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* 6. Atención dedicada */}
      <section className="py-20 md:py-28" data-testid="s-atencion">
        <div className="mx-auto grid max-w-[1300px] items-center gap-12 px-6 md:px-10 lg:grid-cols-[5fr_7fr] lg:gap-20">
          <Reveal className="img-frame aspect-[3/4] w-full max-w-md">
            <Foto src={pick(p.atencion.foto[0], 0) || all(p.atencion.foto[0])[0]} alt="" className="h-full w-full" testid="s-atencion-foto" />
          </Reveal>
          <div>
            <Reveal>
              <Pretitulo center={false}>{p.atencion.pre}</Pretitulo>
              <h2 className="mt-4 font-serif text-3xl font-light tracking-tight text-ink md:text-4xl">{p.atencion.titulo}</h2>
              <p className="mt-4 text-[15px] text-ink-soft">{p.atencion.apoyo}</p>
            </Reveal>
            <div className="mt-10 space-y-0">
              {p.atencion.filas.map((f, i) => {
                const Icono = FILAS_ICONOS[i] || Sparkles;
                return (
                  <Reveal key={f.t} delay={i * 0.06} className="flex items-start gap-5 border-b border-lineas py-6 first:border-t" testid={`s-atencion-fila-${i}`}>
                    <Icono size={22} strokeWidth={1.3} className="mt-1 shrink-0 text-terra" />
                    <div>
                      <h3 className="font-serif text-xl text-ink">{f.t}</h3>
                      <p className="mt-1.5 text-sm leading-relaxed text-ink-soft">{f.d}</p>
                    </div>
                  </Reveal>
                );
              })}
            </div>
          </div>
        </div>
      </section>
      {portal}
    </div>
  );
}